/**
 * @file Today view — daily dashboard with timer widget, stats, today's jobs, tasks, leads
 */

import { getTodaysJobs, getJobs } from '@services/jobs.js';
import { getActiveTimer, getTodaysTimeLogs, formatElapsed, msToHours } from '@services/timer.js';
import { getLeadsNeedingFollowUp, STATUS_OPTIONS, timeAgo } from '@services/leads.js';
import { getTodaysTasks, toggleTask, TASK_TAGS } from '@services/tasks.js';
import { load } from '@state/store.js';
import { renderTimerWidget } from '@components/timer-widget.js';
import { renderJobCard } from '@components/job-card.js';
import { push, navigate } from '@core/router.js';

let _tickInterval = null;

function render() {
  const timer = getActiveTimer();
  const todaysJobs = getTodaysJobs();
  const todaysLogs = getTodaysTimeLogs();
  const totalHoursToday = todaysLogs.reduce((sum, log) => sum + (log.duration || 0), 0);
  const activeJobs = getJobs().filter(j => j.stage !== 'Paid & Closed' && j.stage !== 'Completed');
  const followUpLeads = getLeadsNeedingFollowUp().slice(0, 5);
  const todaysTasks = getTodaysTasks().slice(0, 8);

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const timerHtml = timer ? renderTimerWidget(timer) : '';

  const jobsHtml = todaysJobs.length > 0
    ? todaysJobs.map(j => renderJobCard(j)).join('')
    : `<div class="empty-state">
         <div class="empty-state-icon">\uD83D\uDCC5</div>
         <div class="empty-state-text">No jobs scheduled today</div>
       </div>`;

  const leadsHtml = followUpLeads.length > 0
    ? followUpLeads.map(l => {
        const statusObj = STATUS_OPTIONS[l.status] || STATUS_OPTIONS['new'];
        return `
          <div class="lead-followup-card" data-lead-id="${escHtml(l.id)}">
            <div class="lead-followup-info">
              <div class="lead-followup-name">${escHtml(l.name || 'Unnamed')}</div>
              <div class="lead-followup-meta">
                <span class="badge ${statusObj.color || 'badge-gray'}" style="font-size:0.6rem;">${statusObj.label}</span>
                <span style="color:var(--gray-500);font-size:0.72rem;">${timeAgo(l.lastContacted || l.updatedAt)}</span>
              </div>
            </div>
            ${l.phone ? `<a href="tel:${escHtml(l.phone)}" class="lead-followup-call" onclick="event.stopPropagation();">\uD83D\uDCDE</a>` : ''}
          </div>
        `;
      }).join('')
    : '';

  const tasksHtml = todaysTasks.length > 0
    ? todaysTasks.map(t => {
        const tag = TASK_TAGS[t.tag] || TASK_TAGS['none'];
        const priDot = t.priority === 'high' ? '<span style="color:var(--red);font-size:0.6rem;margin-right:3px;">\u25CF</span>' : '';
        return `
          <div class="today-task-item" data-task-id="${t.id}">
            <label class="task-checkbox-wrap" style="flex-shrink:0;">
              <input type="checkbox" class="today-task-cb" data-task-id="${t.id}" ${t.done ? 'checked' : ''} />
            </label>
            <div style="flex:1;min-width:0;">
              <div style="font-size:0.85rem;${t.done ? 'text-decoration:line-through;opacity:0.5;' : ''}">${priDot}${escHtml(t.text)}</div>
              ${t.tag && t.tag !== 'none' ? `<span class="badge ${tag.color}" style="font-size:0.55rem;">${tag.label}</span>` : ''}
            </div>
          </div>
        `;
      }).join('')
    : '';

  return `
    <div class="view-header">
      <div>
        <h1>Today</h1>
        <div class="view-header-sub">${dateStr}</div>
      </div>
    </div>

    ${timerHtml}

    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-value">${todaysJobs.length}</div>
        <div class="stat-label">Jobs Today</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${msToHours(totalHoursToday)}h</div>
        <div class="stat-label">Hours Logged</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${activeJobs.length}</div>
        <div class="stat-label">Active Jobs</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${timer ? formatElapsed(Math.max(0, Date.now() - timer.startTime - (timer.totalPausedMs || 0) - (timer.paused && timer.pauseStart ? Date.now() - timer.pauseStart : 0))) : '--:--'}</div>
        <div class="stat-label">Active Timer</div>
      </div>
    </div>

    ${todaysTasks.length > 0 ? `
    <h3 class="mb-sm" style="color:var(--gold);font-size:0.85rem;text-transform:uppercase;letter-spacing:0.3px;">
      \u2713 Today's Tasks (${todaysTasks.length})
    </h3>
    <div class="today-task-list" id="today-task-list">
      ${tasksHtml}
    </div>
    ` : ''}

    ${followUpLeads.length > 0 ? `
    <h3 class="mb-sm" style="color:var(--gold);font-size:0.85rem;text-transform:uppercase;letter-spacing:0.3px;">
      \uD83D\uDCDE Leads to Follow Up (${followUpLeads.length})
    </h3>
    <div class="lead-followup-list" id="today-lead-list">
      ${leadsHtml}
    </div>
    ` : ''}

    <h3 class="mb-sm" style="color:var(--gold);font-size:0.85rem;text-transform:uppercase;letter-spacing:0.3px;">
      Today's Jobs
    </h3>
    <div class="job-list" id="today-job-list">
      ${jobsHtml}
    </div>
  `;
}

function escHtml(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function mount() {
  // Wire up job card taps
  const list = document.getElementById('today-job-list');
  if (list) {
    list.addEventListener('click', (e) => {
      const card = e.target.closest('.job-card');
      if (card) push('job-detail', { jobId: card.dataset.jobId });
    });
  }

  // Wire up lead follow-up taps
  const leadList = document.getElementById('today-lead-list');
  if (leadList) {
    leadList.addEventListener('click', (e) => {
      if (e.target.closest('a')) return; // skip call links
      const card = e.target.closest('.lead-followup-card');
      if (card) push('lead-detail', { leadId: card.dataset.leadId });
    });
  }

  // Wire up task checkboxes
  const taskList = document.getElementById('today-task-list');
  if (taskList) {
    taskList.addEventListener('change', (e) => {
      const cb = e.target.closest('.today-task-cb');
      if (cb) {
        toggleTask(cb.dataset.taskId);
        // Re-render after short delay
        setTimeout(() => {
          const container = document.getElementById('view-container');
          if (container) {
            container.innerHTML = render();
            mount();
          }
        }, 300);
      }
    });
  }

  // Wire up timer widget tap
  const widget = document.getElementById('timer-widget');
  if (widget) {
    widget.addEventListener('click', () => navigate('timer'));
  }

  // Tick interval for live timer updates
  _tickInterval = setInterval(() => {
    const timer = getActiveTimer();
    if (!timer) return;
    const pauseMs = timer.paused && timer.pauseStart ? Date.now() - timer.pauseStart : 0;
    const elapsed = Math.max(0, Date.now() - timer.startTime - (timer.totalPausedMs || 0) - pauseMs);
    const timeEl = document.getElementById('timer-widget-time');
    if (timeEl) timeEl.textContent = formatElapsed(elapsed);
    // Update stat card
    const statCards = document.querySelectorAll('.stat-value');
    if (statCards[3]) statCards[3].textContent = formatElapsed(elapsed);
  }, 1000);
}

function unmount() {
  if (_tickInterval) {
    clearInterval(_tickInterval);
    _tickInterval = null;
  }
}

export default { render, mount, unmount };
